import EventUser from 'domains/models/event_user';
import * as UserSchema from 'presentations/api/schema/user';
import User from 'domains/models/user';
import {
  AdminEventUserListItem,
  EventUserListItem,
} from '../schema/event_user';
import { isEmail } from 'infras/validator';
import Team from 'domains/models/team';
import { toListItem } from '../schema/team';
import PendingEventUser from 'domains/models/pending_event_user';
import EventContact from 'domains/models/event_contact';
import { ResponseStatus } from 'domains/models/event/responseStatus';
import {
  UserType,
  UserTypeText,
} from 'domains/models/event_type_user/user_type';
import {
  LocationType,
  LocationTypeText,
} from 'domains/models/event_type/locationType';
import { PaymentMethod } from 'domains/models/event_payment/payment_method';
import { Role, RoleText } from 'domains/models/event_type_user/role';
import { Priority, PriorityText } from 'domains/models/event/priority';
import { DisplayType, DisplayTypeText } from 'domains/models/event/displayType';
import {
  Transparency,
  TransparencyText,
} from 'domains/models/event/transparency';
import {
  VisibilityType,
  VisibilityTypeText,
} from 'domains/models/event/visibilityType';
import { Behavior, BehaviorText } from 'domains/models/event/behavior';
import { Status, StatusText } from 'domains/models/event/status';
import { formatToISO } from 'infras/datetime';

export const eventSharedUsers = (
  eventUsers: EventUser[],
  eventId: number,
  users: User[],
  teams?: Team[],
) => {
  const sharedUsers: EventUserListItem[] = [];
  for (const eventUser of eventUsers) {
    if (eventUser.eventId !== eventId) continue;
    const user = users.find((u) => u.uid === eventUser.userId);
    if (isEmail(eventUser.userId)) {
      const listItem: EventUserListItem = {
        eventUserId: eventUser.id || undefined,
        uid: eventUser.userId,
        name: eventUser.userId,
        profileImage: null,
        role: eventUser.role,
        userType: eventUser.userType,
        isAnonymous: true,
        email: eventUser.userId,
        responseStatus: ResponseStatus.Accepted,
        createdAt: formatToISO(eventUser.createdAt),
      };
      sharedUsers.push(listItem);
    } else {
      if (!user) continue;
      const listItem: EventUserListItem = {
        ...UserSchema.toListItem(user),
        eventUserId: eventUser.id || undefined,
        role: eventUser.role,
        userType: eventUser.userType,
        isAnonymous: false,
        email: user.email,
        responseStatus: ResponseStatus.Accepted,
        createdAt: formatToISO(eventUser.createdAt),
      };
      if (teams && teams.length > 0) {
        const team = teams.find((t) => t.id === eventUser.teamId);
        if (team) listItem.team = toListItem(team);
      }
      sharedUsers.push(listItem);
    }
  }
  sharedUsers.sort((a, b) => {
    return a.userType - b.userType;
  });
  return sharedUsers;
};

export const eventAttendUsers = (
  eventUsers: EventUser[],
  eventId: number,
  users: User[],
  teams?: Team[],
) => {
  const sharedUsers = eventSharedUsers(eventUsers, eventId, users, teams);
  return sharedUsers.filter((u) => u.userType !== UserType.NotifyHost);
};

export const eventVoteUsers = (
  eventId: number,
  eventContacts: EventContact[],
  eventUsers: EventUser[],
  users: User[],
) => {
  const sharedUsers: EventUserListItem[] = [];
  for (const eventUser of eventUsers) {
    if (eventUser.eventId !== eventId) continue;
    const user = users.find((u) => u.uid === eventUser.userId);
    if (isEmail(eventUser.userId)) {
      const contact = eventContacts.find(
        (eventContact) => eventUser.userId === eventContact.email,
      )!;
      const listItem: EventUserListItem = {
        eventUserId: eventUser.id!,
        uid: eventUser.userId,
        name: contact.name,
        profileImage: null,
        role: eventUser.role,
        userType: eventUser.userType,
        isAnonymous: true,
        email: contact.email,
        responseStatus: ResponseStatus.Accepted,
        createdAt: formatToISO(eventUser.createdAt),
      };
      sharedUsers.push(listItem);
    } else {
      if (!user) continue;
      const listItem: EventUserListItem = {
        ...UserSchema.toListItem(user),
        eventUserId: eventUser.id!,
        role: eventUser.role,
        userType: eventUser.userType,
        isAnonymous: false,
        email: user.email,
        responseStatus: ResponseStatus.Accepted,
        createdAt: formatToISO(eventUser.createdAt),
      };
      sharedUsers.push(listItem);
    }
  }
  return sharedUsers;
};

export const pollEventUsers = (
  eventUsers: PendingEventUser[],
  pendingEventId: number,
  users: User[],
) => {
  const sharedUsers: EventUserListItem[] = [];
  for (const eventUser of eventUsers) {
    if (eventUser.pendingEventId !== pendingEventId) continue;
    const user = users.find((u) => u.uid === eventUser.userId);
    if (isEmail(eventUser.userId)) {
      const listItem: EventUserListItem = {
        eventUserId: undefined,
        uid: eventUser.userId,
        name: eventUser.userId,
        profileImage: null,
        role: eventUser.role,
        userType: eventUser.userType,
        isAnonymous: true,
        email: eventUser.userId,
        responseStatus: ResponseStatus.Accepted,
        createdAt: formatToISO(eventUser.createdAt),
      };
      sharedUsers.push(listItem);
    } else {
      if (!user) continue;
      const listItem: EventUserListItem = {
        ...UserSchema.toListItem(user),
        eventUserId: undefined,
        role: eventUser.role,
        userType: eventUser.userType,
        isAnonymous: false,
        email: user.email,
        responseStatus: ResponseStatus.Accepted,
        createdAt: formatToISO(eventUser.createdAt),
      };
      sharedUsers.push(listItem);
    }
  }
  return sharedUsers;
};

export const adminEventSharedUsers = (
  eventUsers: EventUser[],
  eventId: number,
  users: User[],
) => {
  const sharedUsers: AdminEventUserListItem[] = [];
  for (const eventUser of eventUsers) {
    if (eventUser.eventId !== eventId) continue;
    const user = users.find((u) => u.uid === eventUser.userId);
    if (!user) continue;
    const listItem: AdminEventUserListItem = {
      ...UserSchema.toListItem(user),
      role: eventUser.role,
      userType: eventUser.userType,
      email: user.email!,
    };
    sharedUsers.push(listItem);
  }
  sharedUsers.sort((a, b) => {
    return a.userType - b.userType;
  });
  return sharedUsers;
};

export function sortCalendarEmailList(host: User, hostUsers: User[]) {
  let emails = hostUsers.filter((u) => u.uid !== host.uid).map((u) => u.email!);
  emails.unshift(host.email!);
  emails = [...new Set(emails)];
  return emails;
}

export function convertTextToLocationType(
  location:
    | 'inPerson'
    | 'phone'
    | 'zoom'
    | 'googleMeet'
    | 'askInvitee'
    | 'teams'
    | 'custom'
    | 'none',
): LocationType | null {
  switch (location) {
    case 'inPerson':
      return LocationType.InPerson;
    case 'phone':
      return LocationType.Phone;
    case 'zoom':
      return LocationType.Zoom;
    case 'googleMeet':
      return LocationType.GoogleMeet;
    case 'askInvitee':
      return LocationType.AskInvitee;
    case 'teams':
      return LocationType.Teams;
    case 'custom':
      return LocationType.Custom;
    case 'none':
      return null;
    default:
      return null;
  }
}

export function convertPaymentMethodToConstant(
  paymentMethod: 'card' | 'onsite' | 'none',
): PaymentMethod {
  switch (paymentMethod) {
    case 'card':
      return PaymentMethod.Card;
    case 'onsite':
      return PaymentMethod.Onsite;
    default:
      return PaymentMethod.None;
  }
}

export function convertTextToDisplayType(
  displayType: 'private' | 'teamPublic' | 'public',
): DisplayType {
  switch (displayType) {
    case 'private':
      return DisplayType.Private;
    case 'teamPublic':
      return DisplayType.TeamPublic;
    case 'public':
      return DisplayType.Public;
  }
}

export function convertTextToTransparency(
  transparency: TransparencyText,
): Transparency {
  switch (transparency) {
    case TransparencyText.Transparent:
      return Transparency.Transparent;
    case TransparencyText.Opaque:
      return Transparency.Opaque;
  }
}

export function convertTextToBehavior(behavior: BehaviorText): Behavior {
  switch (behavior) {
    case BehaviorText.Event:
      return Behavior.Event;
    case BehaviorText.Work:
      return Behavior.Work;
  }
}

export function convertTextToVisibilityType(
  visibilityType: VisibilityTypeText,
): VisibilityType {
  switch (visibilityType) {
    case VisibilityTypeText.Default:
      return VisibilityType.Default;
    case VisibilityTypeText.Busy:
      return VisibilityType.Busy;
  }
}

export function convertTextToUserType(userType: UserTypeText): UserType {
  switch (userType) {
    case UserTypeText.Host:
      return UserType.Host;
    case UserTypeText.Guest:
      return UserType.Guest;
    case UserTypeText.NotifyHost:
      return UserType.NotifyHost;
  }
}

export function convertUserTypeToText(userType: UserType): UserTypeText {
  switch (userType) {
    case UserType.Host:
      return UserTypeText.Host;
    case UserType.Guest:
      return UserTypeText.Guest;
    case UserType.NotifyHost:
      return UserTypeText.NotifyHost;
  }
}

export function convertTextToRole(role: RoleText): Role {
  switch (role) {
    case RoleText.Admin:
      return Role.Admin;
    case RoleText.Editor:
      return Role.Editor;
    case RoleText.Viewer:
      return Role.Viewer;
  }
}

export function convertRoleToText(role: Role): RoleText {
  switch (role) {
    case Role.Admin:
      return RoleText.Admin;
    case Role.Editor:
      return RoleText.Editor;
    case Role.Viewer:
      return RoleText.Viewer;
  }
}

export function convertTextToPriority(
  priority: PriorityText | null,
): Priority | null {
  if (priority === null) {
    return null;
  }
  switch (priority) {
    case PriorityText.Low:
      return Priority.Low;
    case PriorityText.Middle:
      return Priority.Middle;
    case PriorityText.High:
      return Priority.High;
  }
}

export function convertPriorityToText(
  priority: Priority | null,
): PriorityText | null {
  if (priority === null) {
    return null;
  }
  switch (priority) {
    case Priority.Low:
      return PriorityText.Low;
    case Priority.Middle:
      return PriorityText.Middle;
    case Priority.High:
      return PriorityText.High;
  }
}

export function convertDisplayTypeToText(
  displayType: DisplayType,
): DisplayTypeText {
  switch (displayType) {
    case DisplayType.Private:
      return DisplayTypeText.Private;
    case DisplayType.TeamPublic:
      return DisplayTypeText.TeamPublic;
    case DisplayType.Public:
      return DisplayTypeText.Public;
  }
}

export function convertVisibilityTypeToText(
  visibilityType: VisibilityType,
): VisibilityTypeText {
  switch (visibilityType) {
    case VisibilityType.Default:
      return VisibilityTypeText.Default;
    case VisibilityType.Busy:
      return VisibilityTypeText.Busy;
  }
}

export function convertTransparencyToText(
  transparency: Transparency,
): TransparencyText {
  switch (transparency) {
    case Transparency.Transparent:
      return TransparencyText.Transparent;
    case Transparency.Opaque:
      return TransparencyText.Opaque;
  }
}

export function convertBehaviorToText(behavior: Behavior): BehaviorText {
  switch (behavior) {
    case Behavior.Work:
      return BehaviorText.Work;
    case Behavior.Event:
      return BehaviorText.Event;
  }
}

export function convertLocationTypeToText(
  locationType: LocationType,
): LocationTypeText {
  switch (locationType) {
    case LocationType.InPerson:
      return LocationTypeText.InPerson;
    case LocationType.Phone:
      return LocationTypeText.Phone;
    case LocationType.Zoom:
      return LocationTypeText.Zoom;
    case LocationType.GoogleMeet:
      return LocationTypeText.GoogleMeet;
    case LocationType.AskInvitee:
      return LocationTypeText.AskInvitee;
    case LocationType.Teams:
      return LocationTypeText.Teams;
    case LocationType.Custom:
      return LocationTypeText.Custom;
  }
}

export function convertStatusToText(
  status: Status,
): StatusText.Open | StatusText.Cancel {
  if (status === Status.Open) {
    return StatusText.Open;
  } else {
    return StatusText.Cancel;
  }
}

export function convertBookingStatusToText(
  status: Status,
): StatusText.Request | StatusText.Open | StatusText.Cancel {
  if (status === Status.Request) {
    return StatusText.Request;
  } else if (status === Status.Open) {
    return StatusText.Open;
  } else {
    return StatusText.Cancel;
  }
}

export function convertTextToBookingStatus(
  status: StatusText.Request | StatusText.Open | StatusText.Cancel,
): Status.Request | Status.Open | Status.Cancel {
  if (status === StatusText.Request) {
    return Status.Request;
  } else if (status === StatusText.Open) {
    return Status.Open;
  } else {
    return Status.Cancel;
  }
}

export function convertCancelledByToText(
  userType: UserType | null,
): UserTypeText.Guest | UserTypeText.Host | null {
  if (userType === UserType.Guest) {
    return UserTypeText.Guest;
  } else if (userType === UserType.Host) {
    return UserTypeText.Host;
  } else {
    return null;
  }
}
